// Kurzmeldungen.
//
// Die Region ist eine aria-live-Region: Screenreader lesen Meldungen vor,
// ohne den Fokus zu stehlen. Vorher war der Toast ein stummes <div>.
import { el } from './dom.js';

let region: HTMLElement | null = null;

function ensureRegion(): HTMLElement {
  if (region?.isConnected) return region;
  region = el('div', {
    class: 'toast-region',
    role: 'status',
    'aria-live': 'polite',
    'aria-atomic': 'false',
  });
  document.body.appendChild(region);
  return region;
}

export interface ToastOptions {
  ms?: number;
  variant?: 'default' | 'danger';
  /** Rückgängig-Knopf o.ä. – bleibt sichtbar, solange der Toast steht. */
  action?: { label: string; onClick: () => void };
}

export function toast(message: string, options: ToastOptions = {}): () => void {
  const { ms = 3200, variant = 'default', action } = options;
  const host = ensureRegion();

  const node = el(
    'div',
    {
      class: variant === 'danger' ? 'toast toast-danger' : 'toast',
    },
    [
      el('span', {}, message),
      action
        ? el(
            'button',
            {
              class: 'toast-action',
              type: 'button',
              onclick: () => {
                action.onClick();
                dismiss();
              },
            },
            action.label,
          )
        : null,
    ],
  );

  host.appendChild(node);

  let timer = window.setTimeout(dismiss, ms);

  // Wer den Toast anfasst, will ihn lesen – also nicht wegnehmen.
  node.addEventListener('pointerenter', () => window.clearTimeout(timer));
  node.addEventListener('pointerleave', () => {
    timer = window.setTimeout(dismiss, 1200);
  });

  function dismiss(): void {
    window.clearTimeout(timer);
    if (!node.isConnected) return;
    node.classList.add('is-leaving');
    window.setTimeout(() => node.remove(), 220);
  }

  return dismiss;
}

/** Fehlermeldung – lauter angekündigt, damit sie nicht untergeht. */
export function toastError(message: string): void {
  const host = ensureRegion();
  host.setAttribute('aria-live', 'assertive');
  toast(message, { variant: 'danger', ms: 5000 });
  window.setTimeout(() => host.setAttribute('aria-live', 'polite'), 500);
}
